import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Images, Trash2, Plus, Star, Check } from "lucide-react";

export default function CoachTestimonials() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [result, setResult] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rv, setRv] = useState({ name: "", rating: 5, text: "", package: "" });

  const loadReviews = () => api.get("/coach/reviews")
    .then((r) => setReviews([...r.data].sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1))))
    .catch(() => {});

  const publishReview = async (id) => {
    try {
      await api.post(`/coach/reviews/${id}/publish`);
      toast.success(t("rv.publishedOk"));
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addReview = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/coach/reviews", { ...rv, rating: Number(rv.rating) });
      toast.success(t("rv.saved"));
      setRv({ name: "", rating: 5, text: "", package: "" });
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeReview = async (id) => {
    try {
      await api.delete(`/coach/reviews/${id}`);
      toast.success(t("tm.deleted"));
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
  ];

  const load = () => api.get("/coach/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); loadReviews(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("result", result);
      fd.append("before", beforeFile);
      fd.append("after", afterFile);
      await api.post("/coach/testimonials", fd);
      toast.success(t("tm.saved"));
      setName(""); setResult(""); setBeforeFile(null); setAfterFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/coach/testimonials/${id}`);
      toast.success(t("tm.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout nav={nav}>
      <div className="max-w-3xl space-y-8" data-testid="coach-testimonials-page">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <Images className="w-7 h-7 text-primary" /> {t("tm.title")}
        </h1>
        <p className="text-sm text-muted-foreground -mt-4">{t("tm.hint")}</p>

        <form onSubmit={add} className="kc-card p-5 space-y-4" data-testid="testimonial-form">
          <h2 className="font-bold">{t("tm.add")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("tm.name")}</label>
              <input data-testid="testimonial-name-input" required maxLength={80} value={name}
                onChange={(e) => setName(e.target.value)} className="kc-input mt-1" placeholder="Mario R." />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("tm.result")}</label>
              <input data-testid="testimonial-result-input" required maxLength={120} value={result}
                onChange={(e) => setResult(e.target.value)} className="kc-input mt-1" placeholder="-8 kg in 12 settimane" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 kc-input cursor-pointer text-sm text-muted-foreground" data-testid="testimonial-before-upload">
              <Plus className="w-4 h-4 shrink-0" />
              <span className="truncate">{beforeFile ? beforeFile.name : t("tm.before")}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" required
                data-testid="testimonial-before-input" onChange={(e) => setBeforeFile(e.target.files?.[0] || null)} />
            </label>
            <label className="flex items-center gap-2 kc-input cursor-pointer text-sm text-muted-foreground" data-testid="testimonial-after-upload">
              <Plus className="w-4 h-4 shrink-0" />
              <span className="truncate">{afterFile ? afterFile.name : t("tm.after")}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" required
                data-testid="testimonial-after-input" onChange={(e) => setAfterFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <button data-testid="testimonial-submit-button" type="submit"
            disabled={saving || !name.trim() || !result.trim() || !beforeFile || !afterFile}
            className="kc-btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> {saving ? t("common.loading") : t("tm.add")}
          </button>
        </form>

        <div className="grid sm:grid-cols-2 gap-4" data-testid="testimonials-list">
          {items.length === 0 && <p className="text-muted-foreground text-sm">{t("tm.empty")}</p>}
          {items.map((tm) => (
            <div key={tm._id} className="kc-card overflow-hidden" data-testid={`coach-testimonial-${tm._id}`}>
              <div className="grid grid-cols-2">
                <img src={`/api/testimonials/${tm._id}/photo/before`} alt="Prima" className="w-full aspect-[3/4] object-cover" />
                <img src={`/api/testimonials/${tm._id}/photo/after`} alt="Dopo" className="w-full aspect-[3/4] object-cover" />
              </div>
              <div className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold truncate">{tm.name}</p>
                  <p className="text-sm text-primary font-semibold">{tm.result}</p>
                </div>
                <button data-testid={`testimonial-delete-${tm._id}`} onClick={() => remove(tm._id)} className="kc-btn-ghost !px-2.5 shrink-0">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <section className="space-y-4" data-testid="reviews-section">
          <h2 className="font-bold text-lg uppercase tracking-tight flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" /> {t("rv.title")}
          </h2>
          <p className="text-sm text-muted-foreground -mt-2">{t("rv.hint")}</p>
          <form onSubmit={addReview} className="kc-card p-5 space-y-4" data-testid="review-form">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("tm.name")}</label>
                <input data-testid="review-name-input" required maxLength={80} value={rv.name}
                  onChange={(e) => setRv({ ...rv, name: e.target.value })} className="kc-input mt-1" placeholder="Giulia M." />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("rv.rating")}</label>
                <div className="flex gap-1 mt-2" data-testid="review-rating-input">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} data-testid={`review-star-${n}`} onClick={() => setRv({ ...rv, rating: n })}>
                      <Star className={`w-6 h-6 ${n <= rv.rating ? "fill-primary text-primary" : "text-border"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("rv.package")}</label>
                <input data-testid="review-package-input" maxLength={60} value={rv.package}
                  onChange={(e) => setRv({ ...rv, package: e.target.value })} className="kc-input mt-1" placeholder="AD EVOLVE" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("rv.text")}</label>
              <textarea data-testid="review-text-input" required rows={3} maxLength={600} value={rv.text}
                onChange={(e) => setRv({ ...rv, text: e.target.value })} className="kc-input mt-1"
                placeholder={t("rv.textPlaceholder")} />
            </div>
            <button data-testid="review-submit-button" type="submit" disabled={saving || !rv.name.trim() || !rv.text.trim()}
              className="kc-btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> {saving ? t("common.loading") : t("rv.add")}
            </button>
          </form>
          <div className="space-y-3" data-testid="reviews-list">
            {reviews.length === 0 && <p className="text-muted-foreground text-sm">{t("rv.empty")}</p>}
            {reviews.map((r) => (
              <div key={r._id} className="kc-card p-4 flex items-start justify-between gap-3" data-testid={`coach-review-${r._id}`}>
                <div className="min-w-0">
                  <div className="flex gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= (r.rating || 5) ? "fill-primary text-primary" : "text-border"}`} />
                    ))}
                  </div>
                  {r.status === "pending" && (
                    <span className="kc-badge bg-amber-400/10 text-amber-600 border border-amber-400/40 mb-1" data-testid={`review-pending-badge-${r._id}`}>{t("rv.pendingBadge")}</span>
                  )}
                  <p className="text-sm text-muted-foreground">“{r.text}”</p>
                  <p className="font-bold text-sm mt-1.5">{r.name}{r.package && <span className="text-primary font-semibold text-xs"> · {r.package}</span>}</p>
                </div>
                <div className="flex items-center shrink-0">
                  {r.status === "pending" && (
                    <button data-testid={`review-publish-${r._id}`} onClick={() => publishReview(r._id)}
                      className="kc-btn-ghost !px-2.5" title={t("rv.publish")}>
                      <Check className="w-4 h-4 text-green-600" />
                    </button>
                  )}
                  <button data-testid={`review-delete-${r._id}`} onClick={() => removeReview(r._id)} className="kc-btn-ghost !px-2.5">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
